import { useRef, useState } from "react";

import dayjs, { type Dayjs } from "@eisox/dayjs";

import { useLocale } from "../../Locale";
import type { DateFieldFormat } from "../date-field.type";
import { parseFormat, type FormatPart } from "../helpers/date-format.helper";
import {
  clearSegment,
  eraseCharacter,
  resolveMax,
  segmentAt,
  spinSegment,
  syncEditorState,
  toDisplay,
  toEditorState,
  toSegmentValues,
  toValue,
  typeSequence,
  type SegmentValues,
} from "../helpers/date-segments.helper";

interface UseDateSegmentsOptions {
  value: Dayjs | null;
  onValueChange: (value: Dayjs | null) => void;
  format: DateFieldFormat;
  disabled?: boolean;
  readOnly?: boolean;
}

interface DateSegments {
  parts: readonly FormatPart[];
  getSegmentProps: (index: number) => React.ComponentPropsWithRef<"input">;
  onPaste: (event: React.ClipboardEvent<HTMLElement>) => void;
  isEmpty: boolean;
  isInvalid: boolean;
}

const useDateSegments = ({
  value,
  onValueChange,
  format,
  disabled,
  readOnly,
}: UseDateSegmentsOptions): DateSegments => {
  const locale = useLocale();
  const parsed = parseFormat(format, locale);
  const reference = value ?? dayjs().startOf("day");

  const [state, setState] = useState(() => toEditorState(parsed, value));

  const synced = syncEditorState(state, parsed, value);
  if (synced) setState(synced);

  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const bufferRef = useRef({ index: -1, characters: "" });

  const editable = !disabled && !readOnly;
  const filled = state.values.every(segmentValue => segmentValue != null);

  const commit = (values: SegmentValues) => {
    const next = toValue(parsed, values, reference);
    setState(previous => ({ ...previous, values, emittedKey: next?.valueOf() ?? null }));
    onValueChange(next);
  };

  const focusSegment = (index: number) => {
    const input = inputsRef.current[Math.min(Math.max(index, 0), parsed.segments.length - 1)];
    input?.focus();
    input?.select();
  };

  const readBuffer = (index: number) => (bufferRef.current.index === index ? bufferRef.current.characters : "");

  const typeInto = (index: number, characters: string) => {
    const typed = typeSequence(parsed, state.values, index, readBuffer(index), characters, reference);
    if (!typed.changed) return;

    bufferRef.current = { index: typed.index, characters: typed.buffer };
    commit(typed.values);
    if (typed.index !== index) focusSegment(typed.index);
  };

  const erase = (index: number) => {
    const erased = eraseCharacter(parsed, state.values, index, readBuffer(index));
    bufferRef.current = { index, characters: erased.buffer };
    commit(erased.values);
  };

  const handleKeyDown = (index: number) => (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case "ArrowUp":
      case "ArrowDown":
        if (editable) commit(spinSegment(parsed, state.values, index, event.key === "ArrowUp" ? 1 : -1, reference));
        break;
      case "ArrowLeft":
        focusSegment(index - 1);
        break;
      case "ArrowRight":
        focusSegment(index + 1);
        break;
      case "Home":
        focusSegment(0);
        break;
      case "End":
        focusSegment(parsed.segments.length - 1);
        break;
      case "Backspace":
        if (editable) erase(index);
        event.preventDefault();
        return;
      case "Delete":
        if (editable) commit(clearSegment(state.values, index));
        break;
      default:
        return;
    }

    event.preventDefault();
    bufferRef.current = { index, characters: "" };
  };

  const getSegmentProps = (index: number): React.ComponentPropsWithRef<"input"> => {
    const segment = segmentAt(parsed, index);
    const raw = state.values[index] ?? null;
    const display = toDisplay(segment, raw);

    return {
      ref: node => {
        inputsRef.current[index] = node;
      },
      value: display,
      role: "spinbutton",
      inputMode: segment.type === "meridiem" ? "text" : "numeric",
      autoComplete: "off",
      spellCheck: false,
      disabled,
      readOnly,
      "aria-label": segment.token,
      "aria-valuetext": raw ?? "",
      // A meridiem is a two-state text spinner: numeric bounds would describe nothing.
      ...(segment.type === "meridiem"
        ? {}
        : {
            "aria-valuenow": raw == null ? undefined : Number(raw),
            "aria-valuemin": segment.min,
            "aria-valuemax": resolveMax(parsed, state.values, segment, reference),
          }),
      onFocus: event => {
        bufferRef.current = { index, characters: "" };
        event.currentTarget.select();
      },
      onChange: event => {
        if (!editable) return;
        const next = event.currentTarget.value;
        typeInto(index, next.startsWith(display) ? next.slice(display.length) : next);
      },
      onKeyDown: handleKeyDown(index),
    };
  };

  const onPaste = (event: React.ClipboardEvent<HTMLElement>) => {
    if (!editable) return;

    const pasted = dayjs(event.clipboardData.getData("text"), parsed.pattern, parsed.locale, true);
    if (!pasted.isValid()) return;

    event.preventDefault();
    commit(toSegmentValues(parsed, pasted));
  };

  return {
    parts: parsed.parts,
    getSegmentProps,
    onPaste,
    isEmpty: state.values.every(segmentValue => segmentValue == null),
    isInvalid: filled && toValue(parsed, state.values, reference) === null,
  };
};

export { useDateSegments };
export type { DateSegments, UseDateSegmentsOptions };
